import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getDramaById, getActorById, getRelatedDramas, getGenreByName } from '@/lib/data';
import { dramaComments } from '@/mocks/comments';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';
import StarRating from '@/components/base/StarRating';
import EpisodeList from './components/EpisodeList';
import CommentSection from './components/CommentSection';

export default function Detail() {
  const { id } = useParams();
  const drama = getDramaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  usePageMeta(
    drama ? `${drama.title} - 电视剧详情与剧情介绍 - 韩剧` : '剧集未找到 - 韩剧',
    drama ? `${drama.title}（${drama.year}）剧情简介：${drama.synopsis.slice(0, 60)}…主演 ${drama.cast.map((c) => getActorById(c)?.name).filter(Boolean).join('、')}。` : undefined,
  );

  if (!drama) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
          <i className="ri-file-unknow-line text-2xl"></i>
        </div>
        <h1 className="mt-4 text-xl font-semibold text-foreground-900">没有找到这部剧集</h1>
        <p className="mt-2 text-sm text-foreground-500">它可能已被删除或链接有误。</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-1 cursor-pointer whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors">
          <i className="ri-home-4-line"></i> 返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(drama.id);
  const primaryGenre = drama.genres.map((g) => getGenreByName(g)).find(Boolean);
  const related = getRelatedDramas(drama, 4);
  const comments = dramaComments[drama.id] ?? [];

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleFavorite = () => {
    toggleFavorite(drama.id);
    showToast(favorited ? '已取消收藏' : '已加入我的收藏');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <div className="w-full">
      <section className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <img src={drama.backdrop} alt={`${drama.title} 剧照`} title={`${drama.title} - 韩剧电视剧`} className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative w-full max-w-[1280px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-10 md:pb-14">
          <nav className="flex items-center gap-2 text-xs text-white/70 mb-6">
            <Link to="/" className="hover:text-white cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${primaryGenre?.key ?? 'romance'}`} className="hover:text-white cursor-pointer">{primaryGenre?.name ?? drama.genres[0]}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{drama.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 md:w-56 shrink-0 rounded-lg overflow-hidden border border-white/20 bg-black/30">
              <div className="w-full aspect-[3/4]">
                <img src={drama.poster} alt={`${drama.title} 海报`} title={`${drama.title} - 韩剧电视剧`} className="w-full h-full object-cover object-top" />
              </div>
            </div>

            <div className="flex-1 min-w-0 text-white">
              <h1 className="font-heading font-bold text-3xl md:text-4xl leading-tight">{drama.title}</h1>
              <p className="mt-1.5 text-sm text-white/60 tracking-wide">{drama.titleEn}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/85">
                <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-400"></i> {drama.rating.toFixed(1)} 分</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {drama.year} 年</span>
                <span className="flex items-center gap-1"><i className="ri-stack-line"></i> 全 {drama.episodes} 集</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${drama.status === '更新中' ? 'bg-accent-500' : 'bg-white/20'}`}>{drama.status}</span>
                <span className="flex items-center gap-1"><i className="ri-fire-line text-accent-400"></i> 热度 {(drama.heat / 1000).toFixed(1)}k</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {drama.genres.map((g) => (
                  <span key={g} className="text-xs text-white/85 border border-white/30 rounded-full px-2.5 py-1">{g}</span>
                ))}
                {drama.tags.map((t) => (
                  <span key={t} className="text-xs text-accent-200 bg-accent-500/20 border border-accent-400/40 rounded-full px-2.5 py-1">{t}</span>
                ))}
              </div>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div className="flex gap-2">
                  <dt className="text-white/55 shrink-0">导演</dt>
                  <dd className="text-white/90">{drama.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-white/55 shrink-0">主演</dt>
                  <dd className="text-white/90">
                    {drama.cast.map((c, i) => {
                      const actor = getActorById(c);
                      if (!actor) return null;
                      return (
                        <span key={c}>
                          {i > 0 && '、'}
                          <Link to={`/actor/${actor.id}`} className="hover:text-accent-300 underline-offset-2 hover:underline cursor-pointer">{actor.name}</Link>
                        </span>
                      );
                    })}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleFavorite}
                  className={`cursor-pointer whitespace-nowrap inline-flex items-center gap-2 text-sm font-medium px-6 py-3 rounded-full transition-colors ${
                    favorited ? 'bg-accent-500 text-white' : 'bg-primary-500 hover:bg-primary-600 text-white'
                  }`}
                >
                  <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                  {favorited ? '已收藏' : '收藏该剧'}
                </button>
                <button
                  onClick={handleShare}
                  className="cursor-pointer whitespace-nowrap inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-sm font-medium px-6 py-3 rounded-full backdrop-blur-sm transition-colors"
                >
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          <div className="flex-1 min-w-0 w-full space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-3">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{drama.synopsis}</p>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-xl text-foreground-950">分集剧情</h2>
                <span className="text-xs text-foreground-400">点击集数查看简介</span>
              </div>
              <EpisodeList episodes={drama.episodeList} total={drama.episodes} />
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">演员阵容</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {drama.cast.map((c) => {
                  const actor = getActorById(c);
                  if (!actor) return null;
                  return (
                    <Link
                      key={actor.id}
                      to={`/actor/${actor.id}`}
                      className="group cursor-pointer rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                    >
                      <div className="w-full aspect-[3/4] overflow-hidden bg-background-200">
                        <img src={actor.photo} alt={`${actor.name} 演员`} title={`${actor.name} - 韩剧演员`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-2.5 text-center">
                        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">{actor.name}</h3>
                        <p className="mt-0.5 text-xs text-foreground-400 truncate">{actor.nameEn}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="w-full lg:w-[360px] shrink-0 space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200">
                <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-movie-2-line text-primary-500"></i> 相关推荐
                </h2>
              </div>
              {related.length > 0 ? (
                <ul className="divide-y divide-background-200">
                  {related.map((r) => (
                    <li key={r.id}>
                      <Link to={`/detail/${r.id}`} className="flex gap-3 p-3 hover:bg-background-50 transition-colors cursor-pointer group">
                        <div className="w-16 shrink-0 rounded-md overflow-hidden bg-background-200 aspect-[3/4]">
                          <img src={r.poster} alt={`${r.title} 海报`} title={`${r.title} - 韩剧电视剧`} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0 py-0.5">
                          <h3 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-600 transition-colors">{r.title}</h3>
                          <p className="mt-1 text-xs text-foreground-500 truncate">{r.year} · {r.genres.slice(0, 2).join(' · ')}</p>
                          <div className="mt-1.5"><StarRating rating={r.rating} /></div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="p-5 text-sm text-foreground-500">暂无相关推荐</p>
              )}
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-chat-3-line text-primary-500"></i> 用户评论
                </h2>
                <span className="text-xs text-foreground-400">演示评论 {comments.length}</span>
              </div>
              <CommentSection initial={comments} />
            </section>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-secondary-900 text-white text-sm px-5 py-3 rounded-full animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}